<template>
  <!-- 动态生成的菜单 - 不使用包装div以保持Element Plus折叠功能正常 -->
  <template v-for="menu in menuList" :key="menu.path">
    <!-- 有子菜单的情况 -->
    <el-sub-menu v-if="menu.children && menu.children.length > 0" :index="getMenuIndex(menu)">
      <template #title>
        <el-icon v-if="menu.meta?.icon">
          <Icon :name="resolveIconName(menu.meta.icon)" :size="18" />
        </el-icon>
        <span>{{ getMenuTitle(menu.meta?.title) }}</span>
      </template>
      <!-- 递归渲染子菜单，传递父路径 -->
      <dynamic-menu :menu-list="menu.children" :parent-path="getChildParentPath(menu)" />
    </el-sub-menu>

    <!-- 没有子菜单的菜单项 -->
    <el-menu-item v-else :index="getMenuIndex(menu)">
      <el-icon v-if="menu.meta?.icon">
        <Icon :name="resolveIconName(menu.meta.icon)" :size="18" />
      </el-icon>
      <template #title>
        {{ getMenuTitle(menu.meta?.title) }}
      </template>
    </el-menu-item>
  </template>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePermissionStore } from '@/stores/permission'
import Icon from '@/components/Icon/index.vue'

// 定义props
const props = defineProps({
  menuList: {
    type: Array,
    default: () => []
  },
  // 父路径，用于计算子菜单的完整路径
  parentPath: {
    type: String,
    default: ''
  }
})

// i18n
const { t, te } = useI18n()

// 使用权限store获取动态路由
const permissionStore = usePermissionStore()

// 菜单标题国际化：尝试从 menu.xxx 取翻译，找不到则原样返回
const getMenuTitle = (title) => {
  if (!title) return ''
  // 先尝试 menu.xxx 格式
  const key = `menu.${title}`
  if (te(key)) {
    return t(key)
  }
  // 再尝试直接用中文名作为key（菜单名翻译）
  if (te(title)) {
    return t(title)
  }
  // 找不到翻译则原样返回
  return title
}

// 图标映射表 - Element Plus 图标名称 -> Lucide 图标名称
const iconMap = {
  // Element Plus -> Lucide 映射
  'House': 'Home',
  'OfficeBuilding': 'Building',
  'DataAnalysis': 'BarChart',
  'Grid': 'Grid3x3',
  'Shop': 'ShoppingBag',
  'User': 'User',
  'Document': 'FileText',
  'Money': 'DollarSign',
  'Promotion': 'Megaphone',
  'Bell': 'Bell',
  'Ticket': 'Ticket',
  'TrendCharts': 'TrendingUp',
  'Histogram': 'BarChart',
  'DataBoard': 'LayoutDashboard',
  'Appstore': 'LayoutDashboard',
  'AppStore': 'LayoutDashboard',
  'Smartphone': 'Smartphone',
  'Phone': 'Phone',
  'UserCog': 'UserCog',
  'UserFilled': 'User',
  'Setting': 'Settings',
  'Lock': 'Lock',
  'Menu': 'Menu',
  'Edit': 'Edit',
  'Delete': 'Trash2',
  'Search': 'Search',
  'Plus': 'Plus',
  'VideoPlay': 'Play',
  'Guide': 'Compass',
  'Monitor': 'Monitor',
  'Key': 'Key',
  'List': 'List',
  'Finished': 'CheckCircle',
  'Tickets': 'Tickets',
  'Refresh': 'RefreshCw',
  'Link': 'Link',
  'Tools': 'Settings',
  'Scissor': 'Scissors',
  'Memo': 'FileText',
  'Histogram2': 'BarChart',
  'PieChart': 'PieChart',
  'DataLine': 'LineChart',
  'Files': 'Files',
  'Notebook': 'BookOpen',
  'Reading': 'BookOpen',
  'Calendar': 'Calendar',
  'Clock': 'Clock',
  'Operation': 'Settings',
  'Platform': 'Layers',
  'Upload': 'Upload',
  'Download': 'Download',
  'Sort': 'ArrowUpDown',
  'Filter': 'Filter',
  'Printer': 'Printer',
  'ZoomIn': 'ZoomIn',
  'ZoomOut': 'ZoomOut',
  'View': 'Eye',
  'Hide': 'EyeOff',
  'More': 'MoreHorizontal',
  'ArrowRight': 'ArrowRight',
  'ArrowLeft': 'ArrowLeft',
  'ArrowUp': 'ArrowUp',
  'ArrowDown': 'ArrowDown',
  'Close': 'X',
  'Check': 'Check',
  'CloseBold': 'X',
  'InfoFilled': 'Info',
  'Warning': 'AlertTriangle',
  'Success': 'CheckCircle',
  'WarningFilled': 'AlertTriangle',
  'QuestionFilled': 'HelpCircle',

  // 后端返回的简短名称映射
  'system': 'Settings',
  'user': 'User',
  'peoples': 'Users',
  'tree-table': 'Table',
  'tree': 'GitBranch',
  'build': 'Building',
  'list': 'List',
  'component': 'Package',
  'video': 'Play',
  'example': 'Code',
  'edit': 'Edit',
  'people': 'Users',
  'guide': 'Compass',
  'documentation': 'FileText',
  'form': 'FileEdit',
  'monitor': 'Monitor',
  'chart': 'BarChart',
  'dashboard': 'LayoutDashboard',
  'asset': 'Building',
  'merchant': 'ShoppingBag',
  'contract': 'FileText',
  'operation': 'Settings',
  'payment': 'CreditCard',
  'report': 'BarChart',
  'mall': 'ShoppingBag',
  'tenant': 'Building',
  'marketing': 'Megaphone',
  'energy': 'Zap',
  'workflow': 'GitBranch',
  'Wallet': 'Wallet',
  'wallet': 'Wallet'
}

/**
 * 解析侧栏图标名（映射表优先，否则直通 Lucide 名）
 * @param {string} iconName - 菜单 meta.icon
 * @returns {string} Lucide 图标组件名
 */
const resolveIconName = (iconName) => {
  if (iconMap[iconName]) {
    return iconMap[iconName]
  }
  // 过滤掉 '#' 等无效占位符（旧数据中按钮/菜单用 '#' 兜底）
  if (iconName && iconName !== '' && iconName !== '#') {
    return iconName
  }
  return 'Menu'
}

// 获取菜单的完整路径用于导航
const getMenuIndex = (menu) => {
  // 优先使用 meta.fullPath（由 RouteBuilder 计算）
  if (menu.meta?.fullPath) {
    return menu.meta.fullPath
  }
  // 如果path已经是绝对路径，直接使用
  if (menu.path && menu.path.startsWith('/')) {
    return menu.path
  }
  // 否则拼接父路径
  if (props.parentPath) {
    return `${props.parentPath}/${menu.path}`
  }
  return menu.path || ''
}

// 获取子菜单的父路径
const getChildParentPath = (menu) => {
  // 优先使用 meta.fullPath 作为子菜单的父路径
  if (menu.meta?.fullPath) {
    return menu.meta.fullPath
  }
  if (menu.path && menu.path.startsWith('/')) {
    return menu.path
  }
  if (props.parentPath) {
    return `${props.parentPath}/${menu.path}`
  }
  return menu.path || ''
}

// 过滤掉隐藏的菜单项
const filterHiddenRoutes = (routes) => {
  return routes.filter(route => !route.hidden && !route.meta?.hidden)
}

// 如果没有传入菜单列表，则使用permission store中的动态路由
const menuList = computed(() => {
  if (props.menuList && props.menuList.length > 0) {
    console.log('[DynamicMenu] 使用props.menuList:', props.menuList.length)
    return filterHiddenRoutes(props.menuList)
  }
  // 使用动态生成的路由列表
  const storeRoutes = permissionStore.routes || []
  const filtered = filterHiddenRoutes(storeRoutes)
  console.log('[DynamicMenu] 使用permissionStore.routes:', storeRoutes.length, '过滤后:', filtered.length)
  if (storeRoutes.length > 0 && filtered.length === 0) {
    console.log('[DynamicMenu] 所有路由被过滤，原始数据:', storeRoutes.map(r => ({ path: r.path, hidden: r.hidden, metaHidden: r.meta?.hidden })))
  }
  return filtered
})
</script>

<script>
export default {
  name: 'DynamicMenu'
}
</script>

<style lang="scss" scoped>
// 组件样式可以继承父组件的菜单样式
</style>